import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Popover,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Select,
  MenuItem,
  Switch,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { reportsFilterPillSx, reportsDropdownPaperSx, reportsDropdownSelectedSx, reportsNumberInputSx } from "./reportsFilterStyles";

const OPERATOR_LABELS = { gt: "MORE THAN", lt: "LESS THAN", between: "BETWEEN" };

const segmentedTrackSx = {
  display: "flex",
  bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.045)"),
  borderRadius: "10px",
  p: 0.4,
  mb: 1.5,
  "& .MuiToggleButton-root": {
    flex: 1,
    border: "none",
    borderRadius: "8px !important",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    color: "text.secondary",
    py: 0.7,
    "&.Mui-selected": {
      bgcolor: "background.paper",
      color: "text.primary",
      boxShadow: "0 1px 2px rgba(0,0,0,0.12)",
    },
    "&.Mui-selected:hover": { bgcolor: "background.paper" },
  },
};

const fieldLabelSx = {
  fontSize: "0.68rem",
  fontWeight: 700,
  color: "text.secondary",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  mb: 0.5,
};

// Category drill-down duration/count filter -- redesigned 2026-08-12 to
// match Rahim's bot.space reference exactly: segmented MORE THAN/LESS
// THAN/BETWEEN tabs (+ a Unit dropdown, only for real durations -- a plain
// Count has no unit), Cancel/Apply buttons instead of the Overview table's
// live-bind + confirm-tick pattern. Edits are STAGED locally while the
// popover is open and only committed via onChange on "Apply filter";
// "Cancel" (or closing the popover another way) discards them, reverting
// to whatever was last actually applied -- this is why local state exists
// here at all instead of just calling onChange directly like
// DurationOperatorFilter's callers do.
//
// `units`: array of unit option strings (e.g. ["Seconds","Minutes","Hours"])
// for a real duration filter.
// `includesCount`: folds a "Filter by Count" toggle into THIS SAME popover
// instead of Count being its own separate filter chip -- Rahim's explicit
// correction, 2026-08-12: a standalone Count chip has no visible link to
// which time bracket it's counting within. Redesigned 2026-08-13 (see
// reports-category-drilldown memory) from a min/max threshold into a plain
// on/off toggle -- turning it on tells CategoryReportDetail.jsx to
// collapse the event list to one row per conversation (using whatever
// Duration filter is set above to narrow events first) and show a Count
// column, rather than filtering by a blind count threshold nobody could
// see. Shared by Response Time AND Resolution Time now.
// `value`: { operator, value1, value2, unit, countEnabled? } or null (unset).
const CategoryDurationFilterChip = ({ label, value, units, includesCount, onChange, onRemove }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [staged, setStaged] = useState(null);

  const hasUnits = units && units.length > 0;
  const defaultUnit = hasUnits ? units[0] : "";

  const openPopover = (e) => {
    // Seed staged state from whatever's currently applied, defaulting to
    // "more than 0" (matching bot.space's own default) the very first time.
    setStaged(
      value || { operator: "gt", value1: "0", value2: "", unit: defaultUnit }
    );
    setAnchorEl(e.currentTarget);
  };

  const closeWithoutApplying = () => {
    setAnchorEl(null);
    setStaged(null);
  };

  const applyFilter = () => {
    onChange(staged);
    setAnchorEl(null);
    setStaged(null);
  };

  const setStagedField = (field, val) => setStaged((prev) => ({ ...prev, [field]: val }));

  const unitSuffix = (unit) => (unit ? ` ${unit.toLowerCase()}` : "");

  const summary = () => {
    if (!value || !value.operator) return "Any";
    const { operator, value1, value2, unit, countEnabled } = value;
    let text;
    if (operator === "gt" && value1 !== "") text = `is more than ${value1}${unitSuffix(unit)}`;
    else if (operator === "lt" && value1 !== "") text = `is less than ${value1}${unitSuffix(unit)}`;
    else if (operator === "between" && (value1 !== "" || value2 !== "")) {
      text = `is between ${value1 || 0} and ${value2 || "∞"}${unitSuffix(unit)}`;
    } else {
      text = "Any";
    }
    // Count is a sub-refinement of THIS filter, not its own chip -- append
    // it inline so the connection to the time bracket stays visible.
    if (countEnabled) text += " · Count shown";
    return text;
  };

  const op = staged?.operator || "gt";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={openPopover}
        sx={reportsFilterPillSx}
      >
        {label} {summary()}
      </Button>
      {onRemove && (
        <IconButton size="small" onClick={onRemove} sx={{ p: 0.4, color: "text.secondary" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}

      <Popover
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeWithoutApplying}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        // Was a bare default Popover (Rahim's screenshot, 2026-08-17) --
        // sharp corners, no border/shadow, reading as leftover old UI next
        // to every other dropdown on this page. Matches the rest now.
        slotProps={{ paper: { sx: { mt: 0.75, ...reportsDropdownPaperSx } } }}
      >
        {staged && (
          <Box sx={{ width: 300, maxWidth: "90vw", boxSizing: "border-box", p: 1.75 }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              fullWidth
              value={op}
              onChange={(_, newOp) => newOp && setStagedField("operator", newOp)}
              sx={segmentedTrackSx}
            >
              {Object.entries(OPERATOR_LABELS).map(([val, text]) => (
                <ToggleButton key={val} value={val}>{text}</ToggleButton>
              ))}
            </ToggleButtonGroup>

            <Box sx={{ display: "flex", gap: 1.25 }}>
              {op === "between" ? (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={fieldLabelSx}>From</Typography>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={staged.value1}
                      onChange={(e) => setStagedField("value1", e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={reportsNumberInputSx}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={fieldLabelSx}>To</Typography>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={staged.value2}
                      onChange={(e) => setStagedField("value2", e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={reportsNumberInputSx}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={fieldLabelSx}>{hasUnits ? "Duration" : label}</Typography>
                  <TextField
                    size="small"
                    type="number"
                    fullWidth
                    value={staged.value1}
                    onChange={(e) => setStagedField("value1", e.target.value)}
                    inputProps={{ min: 0 }}
                    sx={reportsNumberInputSx}
                  />
                </Box>
              )}

              {hasUnits && (
                <Box sx={{ flex: 1 }}>
                  <Typography sx={fieldLabelSx}>Unit</Typography>
                  <Select
                    size="small"
                    fullWidth
                    value={staged.unit || defaultUnit}
                    onChange={(e) => setStagedField("unit", e.target.value)}
                    // Dropdown menu restyled (2026-08-17, Rahim's screenshot)
                    // to match Select Category's own popover exactly -- was
                    // a bare default MUI Select menu (sharp corners, no
                    // border/shadow, plain list), reading as leftover old UI
                    // next to every other dropdown on this page.
                    MenuProps={{ slotProps: { paper: { sx: { mt: 0.75, ...reportsDropdownPaperSx } } } }}
                  >
                    {units.map((u) => (
                      <MenuItem
                        key={u}
                        value={u}
                        sx={{
                          fontSize: "0.9rem",
                          fontWeight: (staged.unit || defaultUnit) === u ? 600 : 400,
                          py: 0.95,
                          px: 1.625,
                          mb: 0.25,
                          mx: 0.5,
                          borderRadius: "8px",
                          "&.Mui-selected, &.Mui-selected:hover": reportsDropdownSelectedSx,
                        }}
                      >
                        {u}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}
            </Box>

            {includesCount && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "text.primary" }}>
                      Filter by Count
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mt: 0.25, maxWidth: 210 }}>
                      Groups rows by conversation and shows how many matched.
                    </Typography>
                  </Box>
                  <Switch
                    size="small"
                    checked={Boolean(staged.countEnabled)}
                    onChange={(e) => setStagedField("countEnabled", e.target.checked)}
                  />
                </Box>
              </>
            )}

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, mt: 2 }}>
              <Button size="small" onClick={closeWithoutApplying} sx={{ textTransform: "none", color: "text.secondary" }}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={applyFilter}
                // Brand green (2026-08-17, Rahim's screenshot) -- was
                // bgcolor:"text.primary" (black), the app's one real
                // "confirm/apply" action button reading like a disabled/
                // neutral one instead of the primary green used everywhere
                // else for a confirming action.
                sx={{ textTransform: "none", borderRadius: "8px", bgcolor: "primary.main", color: "primary.contrastText", "&:hover": { bgcolor: "primary.dark" } }}
              >
                Apply filter
              </Button>
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default CategoryDurationFilterChip;
